/**
 * ProtocolNegotiator — administra el protocolo fc-v5 y el tracking de provider_epoch.
 *
 * Responsabilidades:
 *   - Anunciar `clientProtocol = 'fc-v5'` para el session_start.
 *   - Detectar cambios de `provider_epoch` para la misma question_id, que
 *     señalan un voice client fresh y deben resetear el buffer local de audio.
 *   - Helper de envelope (isV5 type-guard).
 */

import {
  WsOutboundEnvelope,
  WsProtocolVersion,
  isV5Envelope,
} from './envelope.types';

export interface ProviderEpochChange {
  /** True si el provider_epoch cambió respecto al último visto para la misma question. */
  changed: boolean;
  /** question_id contra la que se comparó (puede ser undefined si el evento no la trae). */
  questionId?: string;
  /** Valor previo, undefined si nunca se vio. */
  previousEpoch?: number;
  /** Valor nuevo, undefined si el evento no lo trae. */
  currentEpoch?: number;
}

export class ProtocolNegotiator {
  /** Tracking por question_id del último provider_epoch visto. */
  private lastEpochByQuestion: Map<string, number> = new Map();

  /** Snapshot del último epoch visto cuando no hay question_id. */
  private lastEpochGlobal: number | undefined;

  /** Protocolo que el FE anuncia en `session_start`. */
  readonly clientProtocol: WsProtocolVersion = 'fc-v5';

  /**
   * Detecta si el `provider_epoch` cambió respecto al último visto para la
   * misma question_id. Se usa para resetear el buffer de audio cuando el voice
   * client se reconecta fresh (spec §4).
   *
   * Si el envelope no trae provider_epoch, devolvemos changed=false sin alterar
   * estado.
   */
  trackProviderEpoch(envelope: { provider_epoch?: number; question_id?: string }): ProviderEpochChange {
    const epoch = envelope.provider_epoch;
    const qid = envelope.question_id;
    if (typeof epoch !== 'number') {
      return { changed: false, questionId: qid };
    }
    if (qid) {
      const prev = this.lastEpochByQuestion.get(qid);
      this.lastEpochByQuestion.set(qid, epoch);
      const changed = prev !== undefined && prev !== epoch;
      return { changed, questionId: qid, previousEpoch: prev, currentEpoch: epoch };
    }
    const prev = this.lastEpochGlobal;
    this.lastEpochGlobal = epoch;
    const changed = prev !== undefined && prev !== epoch;
    return { changed, previousEpoch: prev, currentEpoch: epoch };
  }

  /** Limpia el tracking de epochs (ej: nueva sesión). */
  resetEpochTracking(): void {
    this.lastEpochByQuestion.clear();
    this.lastEpochGlobal = undefined;
  }

  /**
   * Helper: dado el payload parseado, intenta extraer un envelope v5.
   * Devuelve undefined si no es v5 — el caller decide qué hacer.
   */
  asV5Envelope(payload: unknown): WsOutboundEnvelope | undefined {
    return isV5Envelope(payload) ? payload : undefined;
  }
}
